"use client";

/**
 * React Context wrapper that exposes the V2 design tokens as a runtime-readable theme.
 *
 * In 95% of cases, components consume the theme through Tailwind utility classes derived
 * from the same tokens (e.g. `bg-v2-green-500`, `rounded-v2-md`). For those, this provider
 * is not needed at all — Tailwind is the consumer.
 *
 * The `useTheme()` hook is for the remaining 5% of cases where a colour or size must be
 * a runtime JS value, e.g.:
 *   • Inline `style={{ fill: theme.colors.brand.DEFAULT }}` on dynamic SVGs.
 *   • A chart library that takes a colour array prop.
 *   • Switching a theme variant per user-preference at runtime (future-proofing).
 *
 * The provider's value is `V2_THEME` — the same frozen singleton exported from
 * `./tokens.ts`. We provide it through Context (rather than just importing the constant
 * directly everywhere) so a future light/dark/brand-variant switch only needs to swap the
 * provider's value, without touching every consumer.
 */

import { createContext, useContext } from "react";
import { V2_THEME, type V2Theme } from "./tokens";

const ThemeContext = createContext<V2Theme>(V2_THEME);

interface ThemeProviderProps {
  /**
   * Optional override — defaults to the canonical `V2_THEME`. Pass a different
   * (typed) theme object here to swap palettes for a sub-tree (e.g. an admin shell,
   * a dark variant). Must satisfy the `V2Theme` shape.
   */
  theme?: V2Theme;
  children: React.ReactNode;
}

export function ThemeProvider({ theme = V2_THEME, children }: ThemeProviderProps) {
  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

/**
 * Read the active theme from React Context. Returns the canonical `V2_THEME` if no
 * provider is mounted above the calling component — meaning `useTheme()` is safe to
 * use during SSR, in tests, or in storybook stories without forcing a wrapper.
 */
export function useTheme(): V2Theme {
  return useContext(ThemeContext);
}
